import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "../lib/api";
import { initializeOrganization, setActiveOrgId } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BarChart3, Mail, Lock, User, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ProfitSystemLogo from "../components/ProfitSystemLogo";
import { API } from "../App";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInviteFlow = searchParams.get("invite") === "1";
  const inviteToken = searchParams.get("token") || null;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company_name: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      // Pass invite_token if present so the backend joins the org instead of creating a new one
      if (isInviteFlow && inviteToken) {
        payload.invite_token = inviteToken;
      }
      const response = await axios.post(`${API}/auth/register`, payload, {
        withCredentials: true
      });
      
      localStorage.setItem("auth_token", response.data.token);
      localStorage.setItem("token", response.data.token);

      // If the backend joined an invited org, set it as active
      if (response.data.organization_id) {
        setActiveOrgId(response.data.organization_id, response.data.user?.user_id);
      } else {
        await initializeOrganization(response.data.user?.user_id);
      }

      toast.success("¡Cuenta creada exitosamente!");
      navigate("/dashboard", { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-white">
            <h2 className="text-3xl font-black tracking-tight mb-4">
              Empieza a medir lo que realmente importa
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Conecta tus plataformas de publicidad y descubre la verdad detrás de tus métricas.
            </p>
            <ul className="space-y-4">
              {[
                "Dashboard unificado con todos tus KPIs",
                "Tracking de primera parte sin dependencias",
                "Alertas personalizables en tiempo real",
                "Exportación de reportes profesionales"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-200">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-8">
              <ProfitSystemLogo height={44} />
            </Link>
            
            <h1 className="text-3xl font-black tracking-tight text-slate-900">
              {isInviteFlow ? "Crea tu cuenta para unirte" : "Crea tu cuenta"}
            </h1>
            <p className="text-slate-600 mt-2">
              {isInviteFlow ? "Regístrate para aceptar la invitación al equipo" : "14 días de prueba gratuita · cancela cuando quieras"}
            </p>
          </div>

          {isInviteFlow && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800" data-testid="invite-register-banner">
              Registra tu cuenta con el email al que llegó la invitación.
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-slate-200 hover:bg-slate-50"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Registrarse con Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">O con email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Nombre completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Tu nombre"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-slate-700">Empresa (opcional)</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="company"
                  type="text"
                  placeholder="Tu empresa"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium btn-press"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creando cuenta..." : "Crear Cuenta"}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="text-center text-slate-600">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-red-600 hover:text-red-700 font-medium">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
